import { Component, OnDestroy, OnInit, ViewChild, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Table } from '@interfaces/others';
import { Journey, ColorType, FindJourneyDto, GetJourneyDto } from '@interfaces/rest/interfaces/journey';
import { NavController, PopoverController } from '@ionic/angular';
import { JourneyService } from '@services/journey.service';
import { Subscription } from 'rxjs';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { RestService } from '@services/rest.service';
import { ProvinceService } from '@services/province.service';
import Fuse from 'fuse.js';
import * as moment from 'moment';

@Component({
  selector: 'app-call-all-services',
  templateUrl: './call-all-services.component.html',
  styleUrls: ['./call-all-services.component.scss'],
})
export class CallAllServicesComponent implements OnInit, OnDestroy, OnChanges {

  columns: Table[] = [
    {
      field: 'id',
      name: 'id',
      width: 5
    }, {
      field: 'from',
      name: 'from',
      width: 20,
      class: 'align_left'
    },
    {
      field: 'createdAt',
      name: 'date',
      width: 10,
      type: 'datetime'
    }, {
      field: 'reserveDate',
      name: 'reserveDate',
      width: 10,
      type: 'datetime'
    }, {
      field: 'note',
      name: 'note',
      width: 10
    }, {
      field: 'phone',
      name: 'phone',
      width: 15
    }, {
      field: 'statusObj',
      name: 'status',
      width: 10,
      type: 'multiValColors'
    }, {
      field: 'nameLic',
      name: 'nameLic',
      width: 10
    }, {
      field: 'action',
      name: '',
      width: 10,
      type: 'actions',
      actions: [
        {
          type: 'view'
        },
        {
          type: 'duplicate'
        },
        {
          type: 'close',
          disabledField: 'canEdit' // 🔥 Deshabilitar si canEdit es false (solo para cancelar)
        }
      ]
    }];

  journeys: Journey[] = [];
  filteredJourneys: Journey[] = [];
  transformedJourneys: any[] = [];

  // Paginación
  page = 1;
  limit = 100; // Elementos por página
  totalPages = 1;
  paginatedJourneys: any[] = [];

  // Búsqueda
  searchText: string = '';
  searchLicense: string = '';
  searchStatus: string = '';
  searchReserveType: string = '';
  searchDateType: string = 'creacion'; // 'creacion' o 'reserva'
  searchFromDate: string = '';
  searchFromTime: string = '';
  searchToDate: string = '';
  searchToTime: string = '';
  isSearching: boolean = false;

  // Grupos de estados
  statusGroups = {
    activos: ['DRIVER_FOUND', 'DRIVER_SEARCHING', 'TRIP_STARTED', 'DRIVER_CONFIRMED', 'DRIVER_ARRIVED', 'PENDING', 'NEW'],
    cancelados: ['CANCELED_BY_USER', 'CANCELED_BY_DRIVER', 'CANCELED_BY_MANAGER'],
    completados: ['TRIP_COMPLETED'],
    otros: ['ERROR', 'EXPIRED']
  };

  // Fuse.js para búsqueda inteligente
  private fuse?: Fuse<Journey>;

  private journeysSubscription?: Subscription;
  private provinceId?: number;

  // 🔥 Input para detectar cuando la pestaña está activa
  @Input() isTabActive: boolean = false;
  private lastSyncTime: number = 0;
  private readonly SYNC_COOLDOWN = 2000; // 2 segundos de cooldown entre sincronizaciones

  constructor(
    private nav: NavController,
    private journeyService: JourneyService,
    private utils: DpUtilsService,
    private rest: RestService,
    private provinceService: ProvinceService,
    private popoverController: PopoverController
  ) { }

  async ngOnInit() {
    // Obtener provinceId
    await this.provinceService.getSetProvinceId();
    this.provinceId = this.provinceService.provinceId;

    // Establecer fechas por defecto: 20 horas antes y 20 horas después
    // const now = new Date();
    // const fromDate = new Date(now.getTime() - 20 * 60 * 60 * 1000); // 20 horas antes
    // const toDate = new Date(now.getTime() + 20 * 60 * 60 * 1000); // 20 horas después

    // this.searchFromDate = fromDate.toISOString().split('T')[0];
    // this.searchFromTime = fromDate.toTimeString().slice(0, 5);
    // this.searchToDate = toDate.toISOString().split('T')[0];
    // this.searchToTime = toDate.toTimeString().slice(0, 5);

    // Cargar servicios iniciales (últimos 100)
    if (this.provinceId) {
      await this.journeyService.getAllJourneysInitial(this.provinceId);
    }

    // Suscribirse a actualizaciones
    this.subscribeToJourneys();

    // Inicializar Fuse.js para búsqueda inteligente
    this.initializeFuse();

    // Aplicar filtros iniciales (incluyendo las fechas por defecto)
    this.applyFilters();
  }

  ngOnDestroy() {
    if (this.journeysSubscription) {
      this.journeysSubscription.unsubscribe();
    }
  }

  /**
   * 🔥 Detecta cuando la pestaña se activa y sincroniza los servicios
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['isTabActive'] && this.isTabActive) {
      // Solo sincronizar si la pestaña se acaba de activar (no estaba activa antes)
      const wasActive = changes['isTabActive'].previousValue;
      if (!wasActive && this.provinceId) {
        this.syncLatestJourneys();
      }
    }
  }

  /**
   * 🔥 Sincroniza los últimos 100 servicios cuando se activa la pestaña
   */
  private async syncLatestJourneys() {
    // Evitar múltiples sincronizaciones simultáneas con cooldown
    const now = Date.now();
    if (now - this.lastSyncTime < this.SYNC_COOLDOWN) {
      console.log('⏭️ Sincronización omitida (cooldown activo)');
      return;
    }

    if (!this.provinceId) {
      console.warn('⚠️ No se puede sincronizar: provinceId no disponible');
      return;
    }

    this.lastSyncTime = now;
    console.log('🔄 Iniciando sincronización de servicios...');

    try {
      await this.journeyService.syncLatestJourneys(this.provinceId);
      console.log('✅ Sincronización completada exitosamente');
    } catch (error) {
      console.error('❌ Error al sincronizar servicios:', error);
    }
  }

  subscribeToJourneys() {
    this.journeysSubscription = this.journeyService.getAllJourneys$().subscribe(
      (journeys: Journey[]) => {
        this.journeys = journeys;
        this.initializeFuse();
        this.applyFilters();
      }
    );
  }

  /**
   * 🔥 Inicializa Fuse.js para búsqueda inteligente en direcciones, notas y nombres
   * NO incluye teléfono ni licencia - se buscan por separado
   */
  initializeFuse() {
    if (this.journeys.length === 0) {
      this.fuse = undefined;
      return;
    }

    // Preparar datos para Fuse.js con campos de búsqueda concatenados
    // Solo incluye direcciones, notas y nombres (ver getSearchableText)
    const searchableJourneys = this.journeys.map(j => ({
      ...j,
      searchText: this.getSearchableText(j)
    }));

    this.fuse = new Fuse(searchableJourneys, {
      keys: ['searchText'],
      threshold: 0.2, // Permite errores de escritura
      ignoreLocation: true,
      includeScore: true
    });
  }

  /**
   * 🔥 Normaliza un número de teléfono eliminando caracteres especiales
   */
  private normalizePhoneNumber(phone: string): string {
    if (!phone) return '';
    // Eliminar +, espacios, guiones, paréntesis y otros caracteres especiales
    const normalized = phone.replace(/[\+\s\-\(\)]/g, '');
    return normalized;
  }

  /**
   * 🔥 Obtiene el texto de búsqueda concatenado de un journey (solo direcciones, notas y nombres)
   * NO incluye teléfono ni licencia - se buscan por separado
   */
  private getSearchableText(journey: Journey): string {
    const parts: string[] = [];

    // From address
    if (journey.fromAddress) {
      parts.push(journey.fromAddress.title || '');
      parts.push(journey.fromAddress.description || '');
    }

    // To address
    // if (journey.toAddress) {
    //   parts.push(journey.toAddress.title || '');
    //   parts.push(journey.toAddress.description || '');
    // }

    // Driver name
    if (journey.driver && typeof journey.driver === 'object') {
      parts.push(journey.driver.name || '');
    }

    // Note
    if (journey.note) {
      parts.push(journey.note);
    }

    // NO incluir teléfono aquí - se busca directamente por separado con normalización
    // NO incluir la licencia aquí - se busca por separado con coincidencia exacta

    return parts.join(' ').toLowerCase();
  }

  /**
   * 🔥 Aplica filtros de búsqueda (instantánea, fechas, ID y estado)
   */
  applyFilters() {
    // Resetear a la primera página cuando se aplican filtros
    this.page = 1;

    let filtered = [...this.journeys];
    console.log('🔥 Total journeys antes de filtrar:', filtered.length);

    // Aplicar todos los filtros
    this.applyAllFilters(filtered);
  }

  /**
   * 🔥 Aplica filtros cuando se busca por licencia (coincidencia exacta)
   */
  applyFiltersLicense() {
    // Resetear a la primera página cuando se aplican filtros
    this.page = 1;

    let filtered = [...this.journeys];
    console.log('🔥 Total journeys antes de filtrar (licencia):', filtered.length);

    // Aplicar todos los filtros
    this.applyAllFilters(filtered);
  }

  /**
   * 🔥 Verifica si un texto parece ser un número de teléfono (solo dígitos o con + al inicio)
   */
  private looksLikePhoneNumber(text: string): boolean {
    // Si solo contiene dígitos, espacios, +, guiones o paréntesis, probablemente es un teléfono
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    const digitsOnly = text.replace(/\D/g, '');
    return phoneRegex.test(text) && digitsOnly.length >= 3; // Al menos 6 dígitos
  }

  /**
   * 🔥 Aplica todos los filtros: texto, licencia, estado y fechas
   */
  private applyAllFilters(filtered: Journey[]) {
    // Filtro por texto de búsqueda
    if (this.searchText && this.searchText.trim()) {
      const trimmedText = this.searchText.trim();

      // Si es un número pequeño, buscar por ID (búsqueda exacta)
      const numericId = parseInt(trimmedText);
      if (!isNaN(numericId) && trimmedText === numericId.toString() && numericId < 1000000) {
        filtered = filtered.filter(journey => journey.id === numericId);
        console.log('🔥 Después de filtro por ID:', filtered.length);
      } else if (this.looksLikePhoneNumber(trimmedText)) {
        // Si parece un número de teléfono, hacer búsqueda directa normalizada (búsqueda parcial)
        const normalizedSearchText = this.normalizePhoneNumber(trimmedText);

        filtered = filtered.filter(journey => {
          if (!journey.phone) return false;
          const normalizedPhone = this.normalizePhoneNumber(journey.phone);
          // Búsqueda parcial: el texto de búsqueda debe estar contenido en el teléfono normalizado
          return normalizedPhone.includes(normalizedSearchText);
        });

        console.log('🔥 Después de filtro por teléfono:', filtered.length);
      } else {
        // Búsqueda inteligente con Fuse.js solo para direcciones, notas y nombres
        if (this.fuse) {
          const searchTextLower = trimmedText.toLowerCase();
          const results = this.fuse.search(searchTextLower);
          const searchIds = new Set(results.map(r => r.item.id));
          filtered = filtered.filter(j => searchIds.has(j.id));
          console.log('🔥 Después de filtro por texto (Fuse.js - direcciones/nombres/notas):', filtered.length);
        }
      }
    }

    // Filtro por licencia con coincidencia exacta
    if (this.searchLicense && this.searchLicense.trim()) {
      const trimmedLicense = this.searchLicense.trim();
      const licenseNumber = parseInt(trimmedLicense);

      if (!isNaN(licenseNumber) && trimmedLicense === licenseNumber.toString()) {
        // Búsqueda exacta por número de licencia
        filtered = filtered.filter(journey => {
          if (journey.license && typeof journey.license === 'object' && 'taxiNumber' in journey.license) {
            return journey.license.taxiNumber === licenseNumber;
          }
          return false;
        });
        console.log('🔥 Después de filtro por licencia:', filtered.length);
      } else {
        // Si no es un número válido, no mostrar resultados
        filtered = [];
        console.log('🔥 Licencia no válida, sin resultados');
      }
    }

    // Filtro por estado
    if (this.searchStatus) {
      const statusList = this.statusGroups[this.searchStatus as keyof typeof this.statusGroups];
      if (statusList) {
        filtered = filtered.filter(journey =>
          journey.status && statusList.includes(journey.status)
        );
        console.log('🔥 Después de filtro por estado:', filtered.length);
      }
    }

    // Filtro por tipo de reserva (reservas/al momento)
    if (this.searchReserveType) {
      if (this.searchReserveType === 'reservas') {
        // Solo los que tienen reserveDate
        filtered = filtered.filter(journey => journey.reserveDate != null && journey.reserveDate !== undefined);
        console.log('🔥 Después de filtro por reservas:', filtered.length);
      } else if (this.searchReserveType === 'al-momento') {
        // Solo los que NO tienen reserveDate
        filtered = filtered.filter(journey => !journey.reserveDate || journey.reserveDate === null || journey.reserveDate === undefined);
        console.log('🔥 Después de filtro por al momento:', filtered.length);
      }
    }

    // Filtro por fechas
    if (this.searchFromDate || this.searchToDate) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(journey => {
        // Determinar qué fecha usar según el tipo seleccionado
        let dateToCompare: Date | string | undefined;
        if (this.searchDateType === 'reserva') {
          dateToCompare = journey.reserveDate;
        } else {
          dateToCompare = journey.createdAt;
        }

        if (!dateToCompare) return false;

        // Convertir a Date si es necesario
        const journeyDate = dateToCompare instanceof Date ? dateToCompare : new Date(dateToCompare);

        // Verificar que la fecha sea válida
        if (isNaN(journeyDate.getTime())) {
          console.warn('🔥 Fecha inválida en journey:', journey.id, dateToCompare);
          return false;
        }

        if (this.searchFromDate) {
          const fromDate = this.parseDateTime(this.searchFromDate, this.searchFromTime || '00:00');
          if (journeyDate < fromDate) {
            return false;
          }
        }

        if (this.searchToDate) {
          const toDate = this.parseDateTime(this.searchToDate, this.searchToTime || '23:59');
          // Para la fecha "hasta", incluir todo el día, así que usar 23:59:59
          toDate.setHours(23, 59, 59, 999);
          if (journeyDate > toDate) {
            return false;
          }
        }

        return true;
      });
      console.log('🔥 Después de filtro por fechas:', filtered.length, 'Antes:', beforeFilter);
      console.log('🔥 Fechas:', {
        tipo: this.searchDateType,
        from: this.searchFromDate + ' ' + (this.searchFromTime || '00:00'),
        to: this.searchToDate + ' ' + (this.searchToTime || '23:59'),
        totalJourneys: this.journeys.length
      });
    }

    this.filteredJourneys = filtered;
    this.transformedJourneys = this.getJourneysForTable();
    console.log('🔥 Total después de todos los filtros:', this.transformedJourneys.length);

    // Calcular paginación
    this.calculatePagination();
    console.log('🔥 Paginación:', {
      totalPages: this.totalPages,
      currentPage: this.page,
      paginatedLength: this.paginatedJourneys.length
    });
  }

  /**
   * 🔥 Calcula la paginación y actualiza los servicios paginados
   */
  private calculatePagination() {
    this.totalPages = Math.ceil(this.transformedJourneys.length / this.limit);

    // Asegurar que la página actual sea válida
    if (this.page > this.totalPages && this.totalPages > 0) {
      this.page = this.totalPages;
    }
    if (this.page < 1) {
      this.page = 1;
    }
    if (this.totalPages === 0) {
      this.page = 1;
    }

    const startIndex = (this.page - 1) * this.limit;
    const endIndex = startIndex + this.limit;
    this.paginatedJourneys = this.transformedJourneys.slice(startIndex, endIndex);

    console.log('🔥 calculatePagination:', {
      transformedLength: this.transformedJourneys.length,
      totalPages: this.totalPages,
      currentPage: this.page,
      startIndex,
      endIndex,
      paginatedLength: this.paginatedJourneys.length
    });
  }

  /**
   * 🔥 Maneja el cambio de página
   */
  onPageChange(newPage: number) {
    this.page = newPage;
    this.calculatePagination();
  }

  /**
   * 🔥 Parsea fecha y hora en un objeto Date
   */
  private parseDateTime(dateStr: string, timeStr?: string): Date {
    if (!dateStr) {
      return new Date();
    }

    const date = new Date(dateStr);

    // Si no hay hora especificada, usar inicio del día (00:00) para "desde" o fin del día (23:59) para "hasta"
    if (timeStr) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      date.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      // Si no hay hora, usar inicio del día
      date.setHours(0, 0, 0, 0);
    }

    return date;
  }

  /**
   * 🔥 Búsqueda en base de datos (botón search)
   */
  async searchInDatabase() {
    if (!this.provinceId) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener la provincia',
        cssClass: 'danger'
      });
      return;
    }

    this.isSearching = true;
    try {
      await this.utils.showLoading({ duration: 30000 });

      const searchParams: GetJourneyDto = {
        limit: 200,
        'where.province': this.provinceId,
        'sort_by': 'id:desc'
      };

      // Agregar filtros de fecha si existen
      if (this.searchFromDate) {
        const fromDateTime = this.parseDateTime(this.searchFromDate, this.searchFromTime);
        searchParams.startDate = moment(fromDateTime).format();
      }

      if (this.searchToDate) {
        const toDateTime = this.parseDateTime(this.searchToDate, this.searchToTime);
        searchParams.endDate = moment(toDateTime).format();
      }

      // Agregar filtro por texto o ID si existe
      if (this.searchText && this.searchText.trim()) {
        const trimmedText = this.searchText.trim();
        const numericId = parseInt(trimmedText);

        // Si es un número, buscar por ID
        if (!isNaN(numericId) && trimmedText === numericId.toString()) {
          searchParams['where.id'] = numericId.toString();
        }
        // Si no es numérico, la búsqueda por texto se hace en el cliente con Fuse.js
      }

      // Agregar filtro por estado si existe
      if (this.searchStatus) {
        const statusList = this.statusGroups[this.searchStatus as keyof typeof this.statusGroups];
        if (statusList) {
          searchParams['where.status'] = statusList;
        }
      }

      const resp = await this.rest.getJourneysAll(searchParams);
      await this.utils.hideLoading();

      if (resp.ok && resp.response.data.result) {
        // Agregar los resultados a la lista de todos los servicios
        this.journeyService.addJourneysToAll(resp.response.data.result);

        await this.utils.showToast({
          message: `Se encontraron ${resp.response.data.result.length} servicios`,
          cssClass: 'success'
        });
      } else {
        await this.utils.showToast({
          message: 'Error al buscar servicios',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al buscar servicios:', error);
      await this.utils.showToast({
        message: 'Error al buscar servicios',
        cssClass: 'danger'
      });
    } finally {
      this.isSearching = false;
    }
  }

  /**
   * 🔥 Obtiene el color según el estado del journey
   */
  private getStatusColor(status?: string): ColorType {
    if (!status) {
      return 'white';
    }

    // Estados rojos (cancelados/cerrados/error)
    if (['CLOSED', 'CANCELED_BY_USER', 'CANCELED_BY_DRIVER', 'CANCELED_BY_MANAGER', 'CANCEL', 'ERROR', 'EXPIRED'].includes(status)) {
      return 'red';
    }

    // Estados verdes (completados/confirmados)
    if (['TRIP_COMPLETED'].includes(status)) {
      return 'green';
    }

    // Estados azules (reservas/activos)
    if (['NEW', 'DRIVER_SEARCHING', 'DRIVER_FOUND'].includes(status)) {
      return 'orange';
    }
    // Estados azules (reservas/activos)
    if (['RECURRENT_RESERVE_NEW', 'RESERVE_WAITING', 'TRIP_STARTED', 'PENDING', 'DRIVER_CONFIRMED'].includes(status)) {
      return 'blue';
    }

    // Por defecto blanco
    return 'white';
  }

  /**
   * 🔥 Verifica si un journey está en estado activo (permite editar/duplicar/cancelar)
   */
  private isJourneyActive(journey: Journey): boolean {
    return journey.status === 'NEW' ||
      journey.status === 'DRIVER_SEARCHING' ||
      journey.status === 'DRIVER_FOUND' ||
      journey.status === 'DRIVER_CONFIRMED' ||
      journey.status === 'DRIVER_ARRIVED' ||
      journey.status === 'PENDING' ||
      journey.status === 'TRIP_STARTED';
  }

  /**
   * 🔥 Transforma los journeys para la tabla
   */
  getJourneysForTable(): any[] {
    if (!this.filteredJourneys || this.filteredJourneys.length === 0) {
      return [];
    }

    return this.filteredJourneys.map(journey => {
      const color = this.getStatusColor(journey.status);

      const transformed: any = {
        ...journey,
        from: journey.fromAddress
          ? `${journey.fromAddress.title || ''} - ${journey.fromAddress.description || ''}`.trim()
          : '',
        to: journey.toAddress
          ? `${journey.toAddress.title || ''} - ${journey.toAddress.description || ''}`.trim()
          : '',
        statusObj: journey.status ? [{
          name: journey.status,
          translatePath: 'services',
          color: color
        }] : [],
        // 🔥 Campo para deshabilitar acciones cuando el journey no está activo
        canEdit: this.isJourneyActive(journey),
        color
      };

      return transformed;
    });
  }

  /**
   * 🔥 Maneja las acciones de la tabla
   */
  async doActionParent(ev: any) {
    console.log('doActionParent', ev);
    const journey = ev.line as Journey;

    switch (ev.action) {
      case 'view':
        this.nav.navigateForward(`services/service-detail/${journey.id}`);
        break;
      case 'duplicate':
        await this.confirmDuplicate(journey);
        break;
      case 'close':
        await this.confirmCancel(journey);
        break;
    }
  }

  /**
   * 🔥 Confirma si se quiere duplicar el servicio
   */
  async confirmDuplicate(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Duplicar servicio',
      message: `¿Estás seguro de que quieres duplicar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Duplicar',
      buttonCancel: 'Cancelar'
    });

    if (result) {
      await this.duplicateService(journey);
    }
  }

  /**
   * 🔥 Confirma si se quiere eliminar el servicio
   */
  async confirmCancel(journey: Journey) {
    const result = await this.utils.showAlert({
      header: 'Cancelar servicio',
      message: `¿Estás seguro de que quieres cancelar el servicio #${journey.id}?`,
      cancel: true,
      wait: true,
      cssClass: 'services-modal-confirm',
      buttonOk: 'Cancelar servicio',
      buttonCancel: 'No cancelar'
    });

    if (result) {
      await this.cancelService(journey);
    }
  }

  /**
   * 🔥 Duplica el servicio
   */
  async duplicateService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      const dtoToDuplicate: FindJourneyDto = {
        fromLat: journey.fromLat,
        fromLng: journey.fromLng,
        fromAddress: journey.fromAddress || {
          title: '',
          description: ''
        },
        toLat: journey.toLat,
        toLng: journey.toLng,
        toAddress: journey.toAddress || {
          title: '',
          description: ''
        },
        persons: journey.persons || 1,
        extra: journey.extra || [],
        note: journey.note || '',
        reserve: journey.reserve || false,
        priority: journey.priority || false
      };

      if (journey.reserve && journey.reserveDate) {
        dtoToDuplicate.reserveDate = journey.reserveDate;
      }

      if (journey.userId) {
        dtoToDuplicate.toAnotherUser = journey.userId;
      }

      await this.utils.showLoading({ duration: 20000 });
      const result = await this.rest.findJourney(dtoToDuplicate);
      await this.utils.hideLoading();

      if (result.ok) {
        await this.utils.showToast({
          message: 'Servicio duplicado correctamente',
          cssClass: 'success'
        });
      } else {
        await this.utils.showToast({
          message: 'Error al duplicar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al duplicar servicio:', error);
      await this.utils.showToast({
        message: 'Error al duplicar el servicio',
        cssClass: 'danger'
      });
    }
  }

  /**
   * 🔥 Elimina el servicio (cancela el journey)
   */
  async cancelService(journey: Journey) {
    if (!journey.id) {
      await this.utils.showToast({
        message: 'Error: No se pudo obtener el ID del servicio',
        cssClass: 'danger'
      });
      return;
    }

    try {
      await this.utils.showLoading();
      const resultAction = await this.rest.cancelTotalJourney(journey.id, 'AFTER_ACCEPT');
      await this.utils.hideLoading();

      if (resultAction.ok) {
        await this.utils.showToast({
          message: 'Servicio cancelado',
          cssClass: 'success'
        });
      } else {
        await this.utils.showToast({
          message: 'Error al cancelar el servicio',
          cssClass: 'danger'
        });
      }
    } catch (error) {
      await this.utils.hideLoading();
      console.error('Error al cancelar servicio:', error);
      await this.utils.showToast({
        message: 'Error al cancelar el servicio',
        cssClass: 'danger'
      });
    }
  }

  /**
   * 🔥 Limpia los filtros de búsqueda
   */
  clearFilters() {
    this.searchText = '';
    this.searchLicense = '';
    this.searchStatus = '';
    this.searchReserveType = '';
    this.searchDateType = 'creacion';

    // Restablecer fechas por defecto: 20 horas antes y 20 horas después
    // const now = new Date();
    // const fromDate = new Date(now.getTime() - 20 * 60 * 60 * 1000);
    // const toDate = new Date(now.getTime() + 20 * 60 * 60 * 1000);

    // this.searchFromDate = fromDate.toISOString().split('T')[0];
    // this.searchFromTime = fromDate.toTimeString().slice(0, 5);
    // this.searchToDate = toDate.toISOString().split('T')[0];
    // this.searchToTime = toDate.toTimeString().slice(0, 5);

    // Resetear página a la primera
    this.page = 1;

    this.applyFilters();
  }

  /**
   * 🔥 Selecciona un estado del popover
   */
  async selectStatus(status: string) {
    this.searchStatus = status;
    this.applyFilters();
    // Cerrar el popover
    const popover = await this.popoverController.getTop();
    if (popover) {
      await popover.dismiss();
    }
  }

  /**
   * 🔥 Obtiene el texto del estado seleccionado
   */
  getStatusText(): string {
    if (!this.searchStatus) return 'Estado';
    const statusLabels: { [key: string]: string } = {
      activos: 'Activos',
      cancelados: 'Cancelados',
      completados: 'Completados',
      otros: 'Otros'
    };
    return statusLabels[this.searchStatus] || 'Estado';
  }

  /**
   * 🔥 Calcula el número de fin del rango de paginación
   */
  getPaginationEnd(): number {
    return Math.min(this.page * this.limit, this.transformedJourneys.length);
  }
}

